import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import Card from "@/components/common/Card"
import FormField from "@/components/common/FormField"
import { useForm } from "react-hook-form"
import { errorTypes } from "@/utility/validation"
import type { branch, branchResponse } from "@/interface/branch.interface"
import { useEffect, useState } from "react"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import { RoutesLink } from "@/utility/RouteLinks"
import { BranchService } from "@/services/Branch/BranchSevice"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"

const AddBranch: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<branch>();
    const [isLoading, setIsLoading] = useState(false);
    const onSubmit = async (data: branch) => {
        try {
            setIsLoading(true)
            const payload = {
                name: data.name,
                gst: data.gst,
                phone: data.phone,
                email: data.email,
                secondaryPhone: data.secondaryPhone,
                secondaryEmail: data.secondaryEmail,
                address: data.address,
                city: data.city,
                state: data.state,
                pinCode: data.pinCode,
                pan: data.pan,
                tANDc: data.tANDc,
                shortName: data.shortName,
                status: data.status,
            }
            let response: branchResponse;
            if (id) {
                response = await BranchService?.updateBranch(id, payload);
            } else {
                response = await BranchService?.createBranch(payload);
            }
            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.superAdminBranch)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsLoading(false)
        }
    }
    const handleEdit = async (id: string) => {
        try {
            setIsLoading(true)
            const response = await BranchService?.getBranchById(id);
            if (response?.status) {
                const editData: branch = {
                    name: response?.data?.name,
                    gst: response?.data?.gst,
                    phone: response?.data?.phone,
                    email: response?.data?.email,
                    secondaryPhone: response?.data?.secondaryPhone,
                    secondaryEmail: response?.data?.secondaryEmail,
                    address: response?.data?.address,
                    city: response?.data?.city,
                    state: response?.data?.state,
                    pinCode: response?.data?.pinCode,
                    pan: response?.data?.pan,
                    tANDc: response?.data?.tANDc,
                    shortName: response?.data?.shortName,
                    status: response?.data?.status,
                }
                reset(editData)
                // showToastNotification("success", response?.message)
                // navigate(RoutesLink.superAdminBranch)
            } else {
                showToastNotification("danger", response?.message)
            }
            // console.log(data);
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsLoading(false)
        }
    }
    useEffect(() => {
        if (id) {
            handleEdit(id)
        }
    }, []);
    return (
        <>
            <Layout>
                <div className="space-y-6">
                    <MainHeading title={id ? "Edit Branch" : "Add Branch"} isBackButtonVisible />
                    <Card>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    {...register("name", { ...errorTypes.required('Name') })}
                                    type="text"
                                    name="name"
                                    label="Name"
                                    placeholder="Enter Department Name"
                                    error={errors.name?.message}
                                />

                                <FormField
                                    {...register("gst", { ...errorTypes.required('GST') })}
                                    type="text"
                                    name="gst"
                                    label="GST"
                                    placeholder="Enter GST"
                                    error={errors.gst?.message}
                                />
                                <FormField
                                    {...register("phone", { ...errorTypes.required('Phone') })}
                                    type="text"
                                    name="phone"
                                    label="Phone"
                                    placeholder="Enter Phone"
                                    maxLength={10}
                                    minLength={10}
                                    pattern="[0-9]{10}"
                                    error={errors.phone?.message}

                                />
                                <FormField
                                    {...register("email", { ...errorTypes.required('Email') })}
                                    type="email"
                                    name="email"
                                    label="Email"
                                    placeholder="Enter Email"
                                    error={errors.email?.message}
                                />
                                <FormField
                                    {...register("secondaryPhone")}
                                    type="text"
                                    name="secondaryPhone"
                                    label="Secondary Phone"
                                    placeholder="Enter Secondary Phone"
                                    maxLength={10}
                                    minLength={10}
                                    pattern="[0-9]{10}"
                                    error={errors.secondaryPhone?.message}

                                />
                                <FormField
                                    {...register("secondaryEmail")}
                                    type="email"
                                    name="secondaryEmail"
                                    label="Secondary Email"
                                    placeholder="Enter Secondary Email"
                                    error={errors.secondaryEmail?.message}
                                />
                                <FormField
                                    {...register("address", { ...errorTypes.required('Address') })}
                                    type="text"
                                    name="address"
                                    label="Address"
                                    placeholder="Enter Address"
                                    error={errors.address?.message}
                                />
                                <FormField
                                    {...register("city", { ...errorTypes.required('City') })}
                                    type="text"
                                    name="city"
                                    label="City"
                                    placeholder="Enter City"
                                    error={errors.city?.message}
                                />
                                <FormField
                                    {...register("state", { ...errorTypes.required('State') })}
                                    type="text"
                                    name="state"
                                    label="State"
                                    placeholder="Enter State"
                                    error={errors.state?.message}
                                />
                                <FormField
                                    {...register("pinCode", { ...errorTypes.required('Pin Code') })}
                                    type="text"
                                    name="pinCode"
                                    label="Pin Code"
                                    placeholder="Enter Pin Code"
                                    maxLength={6}
                                    minLength={6}
                                    pattern="[0-9]{6}"
                                    error={errors.pinCode?.message}
                                />
                                <FormField
                                    {...register("pan", { ...errorTypes.required('PAN') })}
                                    type="text"
                                    name="pan"
                                    label="PAN"
                                    placeholder="Enter PAN"
                                    error={errors.pan?.message}

                                />
                                <FormField
                                    {...register("tANDc", { ...errorTypes.required('T&C') })}
                                    type="text"
                                    name="tANDc"
                                    label="T&C"
                                    placeholder="Enter T&C"
                                    error={errors.tANDc?.message}
                                />
                                <FormField
                                    {...register("shortName", { ...errorTypes.required('Short Name') })}
                                    type="text"
                                    name="shortName"
                                    label="Short Name"
                                    placeholder="Enter Short Name"
                                    error={errors.shortName?.message}
                                />
                                <FormField
                                    {...register("status", { ...errorTypes.required('Status') })}
                                    type="select"
                                    value={String(watch("status") ?? "")}
                                    options={[
                                        { value: "select", label: "Select Status" },
                                        { value: "true", label: "True" },
                                        { value: "false", label: "False" },
                                    ]}
                                    name="status"
                                    label="Status"
                                    placeholder="Enter Status"
                                    error={errors.status?.message}
                                />
                            </div>
                            <div className="flex justify-end">
                                <Button disabled={isLoading} type="submit">{isLoading ? "Submitting..." : "Submit"}</Button>

                            </div>
                        </form>
                    </Card>
                </div>
            </Layout>
        </>
    )
}

export default AddBranch